/**
 * TrustedShareDecisionBadge — compact display of *why* Trusted Circle
 * sharing was required or waived for a saved pre-meet checklist.
 *
 * Shows a green "Waived" pill (curated, same-city, documented venue)
 * or an amber "Required" pill, plus a one-line condition summary
 * (✓/✗ for each of the three sub-conditions).
 *
 * Renders nothing for legacy rows that were saved before the decision
 * column existed.
 */
import { CheckCircle2, ShieldCheck, AlertTriangle, X } from "lucide-react";
import type { TrustedShareDecision } from "@/lib/safety/usePreMeet";

interface Props {
  decision: TrustedShareDecision | null;
}

const Cond = ({
  ok,
  label,
}: {
  ok: boolean;
  label: string;
}) => (
  <span
    className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 ${
      ok ? "text-success" : "text-muted-foreground"
    }`}
    title={`${label}: ${ok ? "yes" : "no"}`}
  >
    {ok ? (
      <CheckCircle2 className="h-2.5 w-2.5" />
    ) : (
      <X className="h-2.5 w-2.5" />
    )}
    <span className="text-[10px]">{label}</span>
  </span>
);

const TrustedShareDecisionBadge = ({ decision }: Props) => {
  if (!decision) return null;

  const waived = decision.waived;

  return (
    <div className="mt-2 space-y-1 rounded-lg border border-border bg-secondary/30 p-2 text-[11px]">
      <div className="flex items-center gap-1.5">
        {waived ? (
          <>
            <span className="inline-flex items-center gap-1 rounded-full bg-success/10 text-success px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
              <ShieldCheck className="h-2.5 w-2.5" />
              Trusted share waived
            </span>
            <span className="text-muted-foreground">
              curated venue exemption
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
              <AlertTriangle className="h-2.5 w-2.5" />
              Trusted share required
            </span>
            <span className="text-muted-foreground">
              {decision.share_enabled
                ? `satisfied · ${decision.trusted_count} contact${decision.trusted_count === 1 ? "" : "s"}`
                : "unsatisfied at save"}
            </span>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <Cond ok={decision.venue_is_curated} label="curated" />
        <span className="text-muted-foreground/60">·</span>
        <Cond ok={decision.venue_same_city} label="same city" />
        <span className="text-muted-foreground/60">·</span>
        <Cond ok={decision.venue_has_safety_doc} label="safety docs" />
      </div>
    </div>
  );
};

export default TrustedShareDecisionBadge;
